import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button, Spin, Table, Space, Tooltip, Tag, Dropdown } from "antd";
import { ColumnsType } from "antd/es/table/interface";
import {
  LoadingOutlined,
  PlusOutlined,
  CopyOutlined,
  MailOutlined,
  DownOutlined,
  SendOutlined,
  FormOutlined,
} from "@ant-design/icons";
import { adminContext } from "../../Common/PageRoute";
import PracticeDataRequestService, {
  PracticeDataRequestType,
} from "../../Services/PracticeDataRequestService";
import {
  PageLayout,
  PageHeader,
  SectionCard,
  TableToolbar,
  EmptyState,
} from "../../Common/Layout";
import GenerateRequestModal from "./GenerateRequestModal";
import { Utils } from "../../Utilities/Utils";
import { useResizableColumns } from "../../Common/Custom Components/Ant design/useResizableColumns";

const PracticeDataRequests = () => {
  const { openNotification } = React.useContext(adminContext);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [refresh, setRefresh] = useState(false);
  const [data, setData] = useState<any[]>([]);
  const [dialog, setDialog] = useState<any>({ type: "", params: {} });

  const [pagingItems, setPagingItems] = useState({
    totalRecords: 0,
    currentPage: 1,
    pageSize: 15,
  });

  const [searchQuery, setSearchQuery] = useState("");

  const fetchData = async (
    page = pagingItems.currentPage,
    size = pagingItems.pageSize,
  ) => {
    setLoading(true);
    try {
      const start = (page - 1) * size;
      const res = await PracticeDataRequestService.getPracticeDataRequests(
        start,
        size,
        searchQuery,
      );
      if (res.status && res.result) {
        setData(res.result.items || []);
        setPagingItems({
          ...pagingItems,
          totalRecords: res.result.totalRecords || 0,
          currentPage: page,
          pageSize: size,
        });
      } else {
        openNotification("error", res.message || "Failed to load data");
      }
    } catch (err) {
      openNotification("error", "Error loading data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData(1, pagingItems.pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, refresh]);

  const handleTableChange = (pagination: any) => {
    fetchData(pagination.current, pagination.pageSize);
  };
  const rawColumns: ColumnsType<any> = [
    {
      title: "S.No",
      key: "sno",
      width: 50,
      render: (_: any, __: any, index: number) =>
        (pagingItems.currentPage - 1) * pagingItems.pageSize + index + 1,
    },
    {
      title: "Reference Number",
      dataIndex: "referenceNumber",
      key: "referenceNumber",
      render: (referenceNumber: string, record: any) => {
        const refVal =
          referenceNumber ||
          (record.token ? record.token.substring(0, 8).toUpperCase() : "-");
        return record.id ? (
          <a
            href={`/practice-data-requests/${record?.id}`}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: "#0284c7" }}
          >
            {refVal}
          </a>
        ) : (
          refVal
        );
      },
    },
    {
      title: "Practice Name",
      dataIndex: "practiceName",
      key: "practiceName",
      render: (practice: string, record: any) =>
        practice || record.practiceName || "-",
    },
    {
      title: "Type",
      dataIndex: "requestType",
      key: "requestType",
      render: (requestType: any) => {
        const isInternal =
          requestType === PracticeDataRequestType.Internal ||
          requestType === "Internal" ||
          requestType === 2;
        return (
          <Tag color={isInternal ? "purple" : "blue"}>
            {isInternal ? "Internal" : "External"}
          </Tag>
        );
      },
    },
    {
      title: "Contact Person",
      dataIndex: "contactPersonName",
      key: "contactPersonName",
      render: (contact: string, record: any) =>
        contact || record.contactPersonName || "-",
    },
    {
      title: "Client Email",
      dataIndex: "clientEmail",
      key: "clientEmail",
    },
    {
      title: "Phone",
      dataIndex: "contactPersonPhone",
      key: "contactPersonPhone",
      render: (phone: string, record: any) =>
        phone || record.practicePhoneNumber || "-",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status: any) => {
        // Handle numeric or string representations
        const statusStr =
          typeof status === "number"
            ? ["Unknown", "Pending", "Submitted", "Processed"][status]
            : status;
        let color = "gray";
        if (statusStr === "Pending" || status === 1) color = "orange";
        if (statusStr === "Submitted" || status === 2) color = "green";
        if (statusStr === "Processed" || status === 3) color = "blue";
        return <span style={{ color }}>{statusStr || "Unknown"}</span>;
      },
    },
    {
      title: "Date",
      dataIndex: "createdBy.date",
      key: "CreatedBy.Date",
      render: (_: any, record: any) =>
        Utils.getFormattedDate(record?.createdBy?.date || record?.createdAt),
    },
    {
      title: "Actions",
      key: "actions",
      render: (record: any) => {
        const isInternal =
          record.requestType === PracticeDataRequestType.Internal ||
          record.requestType === "Internal" ||
          record.requestType === 2;
        const isPending = record.status === "Pending" || record.status === 1;
        if (isInternal) {
          return null;
        }
        if (isPending && record.token) {
          return (
            <Space>
              <Tooltip title="Copy Link">
                <Button
                  size="small"
                  icon={<CopyOutlined />}
                  onClick={() => {
                    navigator.clipboard.writeText(
                      `${window.location.origin}/data-request/${record.token}`,
                    );
                    openNotification("success", "Link copied to clipboard!");
                  }}
                />
              </Tooltip>
              <Tooltip title="Resend Email">
                <Button
                  size="small"
                  icon={<MailOutlined />}
                  onClick={async () => {
                    setLoading(true);
                    const res = await PracticeDataRequestService.resendEmail(
                      record.id,
                    );
                    if (res.status) {
                      openNotification("success", "Reminder email sent!");
                    } else {
                      openNotification("error", "Failed to send email");
                    }
                    setLoading(false);
                  }}
                />
              </Tooltip>
            </Space>
          );
        }
        return null;
      },
    },
  ];
  const { columns, components, columnSelector } = useResizableColumns({
    columns: rawColumns,
    storageKey: "ao_practices_dashboard",
  });
  return (
    <>
      <PageLayout>
        <PageHeader
          title="Practice Onboarding"
          subtitle="Manage incoming onboarding requests for new practices"
          actions={[
            <Dropdown
              key="add"
              menu={{
                items: [
                  {
                    key: "external",
                    icon: <SendOutlined />,
                    label: "Send to Client (External)",
                  },
                  {
                    key: "internal",
                    icon: <FormOutlined />,
                    label: "Fill Internally (Internal)",
                  },
                ],
                onClick: ({ key }) =>
                  setDialog({
                    type: "generateLink",
                    requestType:
                      key === "internal"
                        ? PracticeDataRequestType.Internal
                        : PracticeDataRequestType.External,
                  }),
              }}
            >
              <Button type="primary" icon={<PlusOutlined />}>
                Generate Onboarding Link <DownOutlined />
              </Button>
            </Dropdown>,
          ]}
        />
        <SectionCard noPadding>
          <div style={{ padding: "0 16px" }}>
            <TableToolbar
              onSearch={(val) => setSearchQuery(val)}
              searchPlaceholder="Search onboarding requests..."
              onRefresh={() => setRefresh(!refresh)}
              // extra={columnSelector}
            />
          </div>
          <Table
            style={{ margin: "0 16px", marginBottom: 15 }}
            dataSource={data || []}
            columns={columns}
            components={components}
            size="small"
            tableLayout="fixed"
            className="border-top-table"
            scroll={{
              x: "max-content",
              y:
                (data?.length || 0) > 10 ? window.innerHeight - 256 : undefined,
            }}
            loading={{
              spinning: loading,
              indicator: <Spin indicator={<LoadingOutlined />} />,
            }}
            locale={{
              emptyText: (
                <EmptyState
                  title="No practices found"
                  description="Try adjusting your search or date filter"
                />
              ),
            }}
            pagination={{
              current: pagingItems.currentPage,
              pageSize: pagingItems.pageSize,
              total: pagingItems.totalRecords || 0,
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} of ${total} items`,
              showSizeChanger: true,
              pageSizeOptions: ["15", "25", "50", "100"],
            }}
            onChange={handleTableChange}
          />
        </SectionCard>
      </PageLayout>
      {dialog.type === "generateLink" && (
        <GenerateRequestModal
          requestType={dialog.requestType || PracticeDataRequestType.External}
          onDismiss={(added) => {
            setDialog({ type: "" });
            if (added) setRefresh(!refresh);
          }}
          onInternalCreated={(id) => {
            setDialog({ type: "" });
            setRefresh(!refresh);
            if (id) navigate(`/practice-data-requests/${id}`);
          }}
        />
      )}
    </>
  );
};

export default PracticeDataRequests;
